import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  OEMS,
  normalizeSerial,
  type EnrichmentClient,
  type LookupRequest,
  type ProductLookup,
} from './api';
import { SECONDS_PER_LOOKUP } from './bulk';
import { OEM_NAMES, oemName, type Tone } from './format';
import { FIELD, Icon, IconTile, MONO, SrOnly, TONE_TEXT, buttonClass, type IconName } from './ui';

/** The OEM select's "no hint" value. */
const AUTO = '';

export type LookupPhase = 'idle' | 'checking' | 'answered' | 'failed';

export interface LookupState {
  /** Normalized. */
  serial: string;
  phase: LookupPhase;
  request: LookupRequest | null;
  answer: ProductLookup | null;
  /** Why the call itself failed (failed). */
  error: string | null;
}

const IDLE: LookupState = { serial: '', phase: 'idle', request: null, answer: null, error: null };

/**
 * One serial asked of the OEM through POST /v1/products/lookup. `ask` resolves the answer (null
 * when the call failed or a newer ask replaced it); the state says where it is. Leaving the page
 * aborts it.
 */
export function useSerialLookup(client: EnrichmentClient | null) {
  const { t } = useTranslation();
  const [state, setState] = useState<LookupState>(IDLE);
  const requestRef = useRef<AbortController | null>(null);
  useEffect(() => () => requestRef.current?.abort(), []);

  const ask = useCallback(async (request: LookupRequest): Promise<ProductLookup | null> => {
    if (!client) return null;
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    const serial = normalizeSerial(request.serial_number);
    setState({ serial, phase: 'checking', request, answer: null, error: null });
    try {
      const answer = await client.lookup(request, controller.signal);
      if (controller.signal.aborted) return null;
      setState({ serial, phase: 'answered', request, answer, error: null });
      return answer;
    } catch (e) {
      if (!controller.signal.aborted) {
        setState({ serial, phase: 'failed', request, answer: null, error: (e as Error).message || t('Something went wrong.') });
      }
      return null;
    }
  }, [client, t]);

  const reset = useCallback(() => {
    requestRef.current?.abort();
    setState(IDLE);
  }, []);

  return { state, ask, reset };
}

/** The OEM did not know the serial, could not be told, or is not supported. */
const NOT_KNOWN = new Set(['not_found', 'oem_unknown', 'unsupported']);

/** The checking steps; the API does not stream, so they advance on the time spent. */
export const CHECK_STEP_SECONDS = { detect: 3, store: SECONDS_PER_LOOKUP + 2 };

/** The step under way after `elapsed` seconds: 0 detect the OEM, 1 read its site, 2 store. */
export function checkingStep(elapsed: number, oemKnown: boolean): 0 | 1 | 2 {
  if (!oemKnown && elapsed < CHECK_STEP_SECONDS.detect) return 0;
  if (elapsed < CHECK_STEP_SECONDS.store) return 1;
  return 2;
}

/** The checking bar's width (%): it fills over the usual time and never quite ends. */
export function checkingProgress(elapsed: number): number {
  return Math.min(95, Math.round(5 + (elapsed / SECONDS_PER_LOOKUP) * 85));
}

type View = 'ask' | 'checking' | 'needs_model' | 'not_known' | 'failed';

function viewOf(state: LookupState, serial: string): View {
  if (state.phase === 'idle' || state.serial !== serial) return 'ask';
  if (state.phase === 'checking') return 'checking';
  if (state.phase === 'failed') return 'failed';
  const status = state.answer?.status;
  if (status === 'needs_more_info') return 'needs_model';
  if (status && NOT_KNOWN.has(status)) return 'not_known';
  return state.answer?.found ? 'ask' : 'failed';
}

/** The OEM's name for a heading: from the answer, the hint sent, or what was detected. */
function askedOem(state: LookupState): string | null {
  return oemName(state.answer?.oem)
    || oemName(state.request?.oem)
    || oemName(state.answer?.check?.detection?.oem ?? null);
}

/**
 * DOC 3: a search that looks like a serial and matches nothing stored. Ask the OEM (with an
 * optional OEM hint), then the checking steps, then what the OEM said when it was not a product:
 * it needs the model number (ask again with it), it does not know the serial, or the check
 * failed (try again). A found product opens in the drawer; the caller does that.
 */
export function LookupPanel({ search, lookup, elapsed, onAsk, onReset }: {
  /** The search as typed. */
  readonly search: string;
  readonly lookup: LookupState;
  readonly elapsed: number;
  readonly onAsk: (request: LookupRequest) => void;
  readonly onReset: () => void;
}) {
  const { t } = useTranslation();
  const serial = normalizeSerial(search);
  const view = viewOf(lookup, serial);
  const [oem, setOem] = useState<string>(AUTO);
  const [model, setModel] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const oemId = useId();
  const modelId = useId();
  const oemText = askedOem(lookup);

  // The button pressed is gone with the view it was in: take focus to the new view's heading.
  useEffect(() => {
    const active = document.activeElement;
    if (!active || active === document.body || panelRef.current?.contains(active)) headingRef.current?.focus();
  }, [view]);
  useEffect(() => {
    if (view !== 'needs_model') setModel('');
  }, [view]);
  // A hint chosen for one serial is not carried to the next.
  useEffect(() => setOem(AUTO), [serial]);

  const hint = oem === AUTO ? {} : { oem };
  const oemSelect = (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={oemId} className="text-xs font-semibold text-[#434A59] dark:text-[#c9d1d9]">{t('OEM')}</label>
      <select id={oemId} className={`${FIELD} w-[220px]`} value={oem} onChange={(event) => setOem(event.target.value)}>
        <option value={AUTO}>{t('Detect automatically')}</option>
        {OEMS.map((code) => <option key={code} value={code}>{OEM_NAMES[code]}</option>)}
      </select>
    </div>
  );
  const mono = <span className={MONO}>{serial}</span>;
  const heading = (children: ReactNode) => (
    <h2 ref={headingRef} tabIndex={-1} className="m-0 mb-1 text-base font-bold text-[#0B1B3D] dark:text-[#e6edf3] outline-none">{children}</h2>
  );

  let body: ReactNode;
  let announce: string;
  if (view === 'checking') {
    announce = t('Checking {{serial}} with the OEM', { serial });
    body = (
      <div className="max-w-[760px]">
        {heading(<>{t('Checking')} {mono} {t('with the OEM')}</>)}
        <p className="m-0 mb-[18px] text-sm text-[#5C6B85] dark:text-[#8b949e]">{t('About 20 seconds. You can keep working; the result opens when it is ready.')}</p>
        <CheckingSteps oem={oemName(lookup.request?.oem)} elapsed={elapsed} />
      </div>
    );
  } else if (view === 'needs_model') {
    const who = oemText ?? t('The OEM');
    announce = t('{{oem}} needs the model number', { oem: who });
    body = (
      <Message icon="alert" tone="warn">
        {heading(<>{t('{{oem}} needs the model number for', { oem: who })} {mono}</>)}
        <p className="m-0 mb-4 text-sm leading-normal text-[#5C6B85] dark:text-[#8b949e]">
          {t('Some serials are shared across models. Add the model number from the unit’s label and ask again. Nothing was stored.')}
        </p>
        <form
          className="flex flex-wrap items-end gap-2.5"
          onSubmit={(event) => {
            event.preventDefault();
            if (!model.trim() || !lookup.request) return;
            // The OEM it was asked of, or the one it detected, so the model number goes to the same site.
            const code = lookup.request.oem || lookup.answer?.oem?.code || lookup.answer?.check?.detection?.oem;
            onAsk({ serial_number: lookup.request.serial_number, ...(code ? { oem: code } : {}), model_number: model.trim() });
          }}
        >
          <div className="flex flex-col gap-1.5">
            <label htmlFor={modelId} className="text-xs font-semibold text-[#434A59] dark:text-[#c9d1d9]">{t('Model number')}</label>
            <input
              id={modelId}
              className={`${FIELD} w-[260px]`}
              type="text"
              placeholder={t('e.g. as printed on the label')}
              value={model}
              onChange={(event) => setModel(event.target.value)}
            />
          </div>
          <button type="submit" className={buttonClass({ primary: true })} disabled={!model.trim()}>
            {oemText ? t('Ask {{oem}} again', { oem: oemText }) : t('Ask the OEM again')}
          </button>
          <button type="button" className={buttonClass()} onClick={onReset}>{t('Cancel')}</button>
        </form>
      </Message>
    );
  } else if (view === 'not_known') {
    const status = lookup.answer?.status;
    const who = oemText ?? t('The OEM');
    announce = t('{{oem}} does not know {{serial}}', { oem: who, serial });
    let why = t('Its warranty site has no record of this serial. Check it for typos, or pick the OEM and ask again.');
    if (status === 'oem_unknown') why = t('The OEM could not be told from this serial. Pick the OEM and ask again.');
    if (status === 'unsupported') why = t('This OEM’s warranty site is not supported yet.');
    body = (
      <Message icon="search" tone="neutral">
        {heading(<>{t('{{oem}} does not know the serial', { oem: who })} {mono}</>)}
        <p className="m-0 mb-4 text-sm leading-normal text-[#5C6B85] dark:text-[#8b949e]">{why} {t('No product was stored.')}</p>
        <div className="flex flex-wrap items-end gap-2.5">
          {oemSelect}
          <button
            type="button"
            className={buttonClass({ primary: true })}
            onClick={() => onAsk({ serial_number: lookup.request?.serial_number ?? search.trim(), ...hint, refresh: true })}
          >
            {t('Ask again')}
          </button>
          <button type="button" className={buttonClass()} onClick={onReset}>{t('Back')}</button>
        </div>
      </Message>
    );
  } else if (view === 'failed') {
    const check = lookup.answer?.check;
    const reason = lookup.error || check?.error?.message || check?.error?.code || t('The OEM site did not answer.');
    announce = t('The check of {{serial}} failed', { serial });
    body = (
      <Message icon="alert" tone="bad">
        {heading(<>{t('The check of')} {mono} {t('did not finish')}</>)}
        <p role="alert" className="m-0 mb-4 text-sm leading-normal text-[#5C6B85] dark:text-[#8b949e]">{reason} {t('Nothing was stored.')}</p>
        <div className="flex flex-wrap items-end gap-2.5">
          <button type="button" className={buttonClass({ primary: true })} onClick={() => lookup.request && onAsk(lookup.request)}>
            {t('Try again')}
          </button>
          <button type="button" className={buttonClass()} onClick={onReset}>{t('Back')}</button>
        </div>
      </Message>
    );
  } else {
    announce = '';
    body = (
      <Message icon="search" tone="info">
        {heading(<>{t('No stored product has the serial')} {mono}</>)}
        <p className="m-0 mb-4 text-sm leading-normal text-[#5C6B85] dark:text-[#8b949e]">
          {t('Ask the OEM: we read its warranty site, store the answer for your tenant, and show it here. It takes about 20 seconds.')}
        </p>
        <div className="flex flex-wrap items-end gap-2.5">
          {oemSelect}
          <button type="button" className={buttonClass({ primary: true })} onClick={() => onAsk({ serial_number: search.trim(), ...hint })}>
            {t('Ask the OEM')}
          </button>
        </div>
      </Message>
    );
  }

  return (
    <div ref={panelRef} className="px-6 pb-8 pt-7">
      <SrOnly role="status">{announce}</SrOnly>
      {body}
    </div>
  );
}

function Message({ icon, tone, children }: {
  readonly icon: IconName;
  readonly tone: Tone;
  readonly children: ReactNode;
}) {
  return (
    <div className="flex max-w-[760px] items-start gap-4">
      <IconTile name={icon} tone={tone} size={40} iconSize={20} />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

/** The checking bar and its three steps; the step under way shows the seconds spent. */
export function CheckingSteps({ oem, elapsed }: { readonly oem: string | null; readonly elapsed: number }) {
  const { t } = useTranslation();
  const step = checkingStep(elapsed, Boolean(oem));
  const steps = [
    step > 0 ? (oem ? t('OEM: {{oem}}', { oem }) : t('OEM detected')) : t('Detecting the OEM'),
    oem ? t('Reading {{oem}}’s warranty site', { oem }) : t('Reading the OEM’s warranty site'),
    t('Storing the answer for your tenant'),
  ];
  return (
    <>
      <div className="mb-[18px] h-1.5 overflow-hidden rounded-full bg-[#F1F2F5] dark:bg-[#21262d]">
        <div className="h-1.5 rounded-full bg-[#0B1B3D] dark:bg-[#e6edf3] transition-[width] duration-1000" style={{ width: `${checkingProgress(elapsed)}%` }} />
      </div>
      <ol aria-label={t('Steps')} className="m-0 flex list-none flex-col gap-3 p-0">
        {steps.map((label, index) => {
          const done = index < step;
          const current = index === step;
          return (
            <li key={index} aria-current={current ? 'step' : undefined} className={`flex items-center gap-2.5 text-[13px] ${!done && !current ? 'text-[#5C6B85] dark:text-[#8b949e]' : 'text-[#0B1B3D] dark:text-[#e6edf3]'}`}>
              {done && (
                <span className="inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-[#D1FAE5] dark:bg-[#10B981]/15">
                  <Icon name="check" size={12} className={TONE_TEXT.ok} strokeWidth={3} />
                </span>
              )}
              {current && (
                <span className="inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-[#0B1B3D] dark:bg-[#e6edf3]">
                  <span className="size-1.5 rounded-full bg-white dark:bg-[#0d1117]" />
                </span>
              )}
              {!done && !current && <span className="box-border inline-flex size-5 shrink-0 rounded-full border-2 border-solid border-[#CBD5E1] dark:border-[#454c56]" />}
              {current ? <strong>{label}</strong> : label}
              {current && <span className="text-[#5C6B85] dark:text-[#8b949e]">{t('{{seconds}} s', { seconds: elapsed })}</span>}
            </li>
          );
        })}
      </ol>
    </>
  );
}
